import {
  Alert,
  Box,
  Button,
  Center,
  Divider,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../lib/api.js";
import logoDunkelUrl from "../assets/mindsquare-logo-dunkel.svg";
import logoUrl from "../assets/mindsquare-logo.svg";

interface DevLoginBody {
  email: string;
}

interface AuthModeResponse {
  mode: "local" | "entra";
}

export function LoginPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const router = useRouter();

  // Vom Backend abfragen, ob lokal-Login oder Entra-Redirect anzuzeigen ist.
  const { data: authMode, isLoading: authModeLoading } = useQuery<AuthModeResponse>({
    queryKey: ["auth", "mode"],
    queryFn: () => api.get<AuthModeResponse>("/auth/mode"),
    staleTime: Infinity,
  });

  const form = useForm<DevLoginBody>({
    initialValues: { email: "" },
    validate: {
      email: (v) =>
        /^\S+@\S+$/.test(v) ? null : t("auth.login.emailError"),
    },
  });

  const mutation = useMutation({
    meta: { silent: true },
    mutationFn: (values: DevLoginBody) =>
      api.post("/auth/dev-login", values),
    onSuccess: async () => {
      // refetchQueries (statt invalidateQueries) refetcht auch ohne aktive
      // Observer und wartet auf die Antwort — sonst rendert AuthGuard nach
      // dem Navigate mit dem alten gecachten null-Wert und schickt zurück
      // auf /login.
      await queryClient.refetchQueries({ queryKey: ["auth", "me"] });
      await router.navigate({ to: "/" });
    },
    onError: (error) => {
      const message =
        error instanceof ApiError ? error.message : t("auth.login.errorTitle");
      notifications.show({
        title: t("auth.login.errorTitle"),
        message: t("auth.login.errorMessage", { message }),
        color: "red",
      });
    },
  });

  const isEntra = authMode?.mode === "entra";
  // Die API schickt abgelehnte Entra-Anmeldungen hierher zurück (LOGIN_ALLOWLIST).
  const keinZugang =
    new URLSearchParams(window.location.search).get("fehler") === "kein-zugang";

  return (
    <Center h="100vh">
      <Paper shadow="md" p="xl" w={400}>
        <Stack>
          <Box
            component="img"
            src={logoUrl}
            alt="mindsquare"
            darkHidden
            style={{ height: 44, alignSelf: "flex-start" }}
          />
          <Box
            component="img"
            src={logoDunkelUrl}
            alt="mindsquare"
            lightHidden
            style={{ height: 44, alignSelf: "flex-start" }}
          />
          <Title order={2}>{t("auth.login.title")}</Title>
          {/*
            Der Untertitel folgt dem Anmeldemodus, nicht der Vermutung.

            Er stand fest auf „Servicekunden-CRM — Entwicklungs-Login",
            waehrend nur der Knopf darunter nach `isEntra` verzweigte. Auf
            Staging (`AUTH_MODE=entra`) las man also „Entwicklungs-Login" ueber
            einem Knopf, der zu Microsoft weiterleitet — die Seite widersprach
            sich selbst und stellte eine produktionsnahe Umgebung als
            Entwicklerzugang dar.

            Solange der Modus noch geladen wird, steht bewusst der neutrale
            Untertitel: Er stimmt in beiden Modi, also gibt es kein Bild, in
            dem die Seite etwas Falsches behauptet und es danach zurueckzieht.
          */}
          <Text c="dimmed" size="sm">
            {authModeLoading
              ? t("auth.login.subtitle")
              : isEntra
                ? t("auth.login.subtitleEntra")
                : t("auth.login.subtitleLocal")}
          </Text>

          {keinZugang && (
            <Alert color="red" title={t("auth.login.notAllowedTitle")}>
              {t("auth.login.notAllowedMessage")}
            </Alert>
          )}

          {authModeLoading ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : isEntra ? (
            <Stack>
              <Button
                component="a"
                href="/api/v1/auth/entra/login"
                fullWidth
                size="md"
              >
                {t("auth.login.entraButton")}
              </Button>
              <Text size="xs" c="dimmed" ta="center">
                {t("auth.login.entraHint")}
              </Text>
            </Stack>
          ) : (
            <>
              <form
                onSubmit={form.onSubmit((values) => mutation.mutate(values))}
              >
                <Stack>
                  <TextInput
                    label={t("auth.login.emailLabel")}
                    type="email"
                    placeholder={t("auth.login.emailPlaceholder")}
                    {...form.getInputProps("email")}
                  />
                  <Button type="submit" loading={mutation.isPending} fullWidth>
                    {t("auth.login.submitButton")}
                  </Button>
                </Stack>
              </form>
              <Divider label={t("auth.login.devModeLabel")} labelPosition="center" />
              <Text size="xs" c="dimmed" ta="center">
                {t("auth.login.devModeHint")}
              </Text>
            </>
          )}
        </Stack>
      </Paper>
    </Center>
  );
}
